import { useEffect, useRef } from 'react';
import type { TraceLine } from '../api';

/** Fields already rendered in their own column — don't repeat them inline. */
const RENDERED = new Set([
  'id',
  'ts',
  'level',
  'msg',
  'event',
  'conversation_id',
  'trace_id',
  'svc',
  'role',
]);

function extras(line: TraceLine): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(line)) {
    if (RENDERED.has(k) || v === null || v === undefined) continue;
    parts.push(`${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`);
  }
  return parts.join('  ');
}

const time = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(11, 23)
    : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(
        d.getSeconds(),
      ).padStart(2, '0')}.${String(d.getMilliseconds()).padStart(3, '0')}`;
};

type Props = {
  lines: TraceLine[];
  follow: boolean;
  onUnfollow: () => void;
};

export function LogList({ lines, follow, onUnfollow }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);

  // Keep the newest line in view while following.
  useEffect(() => {
    if (follow && ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [lines, follow]);

  // Scrolling up disengages follow, the way every tail UI behaves. Without this
  // you cannot read backwards during a live call.
  const onScroll = (): void => {
    const el = ref.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (pinned.current && !atBottom && follow) onUnfollow();
    pinned.current = atBottom;
  };

  if (!lines.length) {
    return (
      <div className="logs">
        <div className="empty">No lines for this call.</div>
      </div>
    );
  }

  return (
    <div className="logs" ref={ref} onScroll={onScroll}>
      {lines.map((line) => {
        const level = (line.level ?? 'info').toLowerCase();
        const cls =
          level === 'error' || level === 'fatal'
            ? 'line line--error'
            : level === 'warn'
              ? 'line line--warn'
              : line.event
                ? 'line line--event'
                : 'line';
        const rest = extras(line);
        const dropped = line.event === 'trace.dropped';

        return (
          <div key={line.id}>
            {dropped && <div className="gap">— lines dropped ({String(line.count ?? '?')}) —</div>}
            <div className={cls}>
              <span className="line__ts">{time(line.ts)}</span>
              <span className="line__level">{level}</span>
              <span className="line__msg">
                {line.event && <span className="line__event">{line.event}</span>}
                {line.msg}
                {rest && <span className="line__fields">{rest}</span>}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
